import { useState } from "react";
import { X, Sparkles, Loader2, Check, ChevronLeft } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const DAY_NAMES = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const MONTHS_IT = ["gennaio","febbraio","marzo","aprile","maggio","giugno",
                   "luglio","agosto","settembre","ottobre","novembre","dicembre"];
function formatItDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return `${DAY_NAMES[(d.getDay()+6)%7]} ${d.getDate()}/${(d.getMonth()+1).toString().padStart(2,"0")}/${d.getFullYear()}`;
}

/**
 * Modal per definire una ricorrenza complessa in linguaggio naturale italiano.
 * Chiama /api/recurrence/interpret (LLM Claude sonnet 4-6) → RRULE + anteprima.
 */
export default function RecurrenceEditor({ open, onClose, onSave, baseDate, initialRule = "", initialDescription = "" }) {
  const [nl, setNl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { rrule, human_readable, next_5, confidence, warnings, error, suggestions }

  if (!open) return null;

  const bd = baseDate || new Date().toISOString().slice(0, 10);

  const interpret = async () => {
    if (nl.trim().length < 2) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.recurrenceInterpret(nl.trim(), bd);
      setResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore interpretazione. Riprova.");
    } finally { setBusy(false); }
  };

  const confirm = () => {
    if (!result || !result.rrule) return;
    onSave({ rrule: result.rrule, description: result.human_readable });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="recurrence-editor">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--pxos-border)" }}>
          <div className="flex items-center gap-2">
            <Sparkles size={16} style={{ color: "var(--pxos-primary-accent)" }} />
            <h3 className="font-display text-base font-semibold" style={{ color: "var(--pxos-text-dark)" }}>Ripetizione personalizzata</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-black/5" aria-label="Chiudi" data-testid="recurrence-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest opacity-70" style={{ color: "var(--pxos-text-dark)" }}>
              Descrivi a parole
            </label>
            <textarea
              value={nl}
              onChange={(e) => setNl(e.target.value)}
              rows={3}
              className="pxos-input mt-1 w-full text-sm"
              placeholder='Es. "ogni primo e ultimo lunedì del mese" o "ogni martedì e giovedì per 10 volte"'
              data-testid="recurrence-nl-input"
              autoFocus
            />
            {initialRule && !result && (
              <div className="text-xs mt-2 opacity-70" style={{ color: "var(--pxos-text-dark)" }}>
                Regola attuale: <span className="font-mono">{initialRule}</span>
                {initialDescription && <span> · {initialDescription}</span>}
              </div>
            )}
          </div>

          <button
            onClick={interpret}
            disabled={busy || nl.trim().length < 2}
            className="pxos-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="recurrence-interpret-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {busy ? "Interpretazione…" : "Interpreta"}
          </button>

          {result && result.rrule && (
            <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: "#B6E0C4", background: "#F0FBF4" }} data-testid="recurrence-result">
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-70" style={{ color: "#1F7A2C" }}>Interpretazione</div>
                <div className="text-sm font-medium" style={{ color: "var(--pxos-text-dark)" }} data-testid="recurrence-hr">{result.human_readable}</div>
                <div className="text-[10px] font-mono opacity-70 mt-0.5" style={{ color: "var(--pxos-text-dark)" }} data-testid="recurrence-rrule">
                  RRULE: {result.rrule}
                </div>
              </div>

              {result.warnings && result.warnings.length > 0 && (
                <div className="text-xs" style={{ color: "#8A5A00" }}>
                  ⚠ {result.warnings.join(" · ")}
                </div>
              )}

              {result.next_5_occurrences && result.next_5_occurrences.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1" style={{ color: "var(--pxos-text-dark)" }}>Prossime occorrenze</div>
                  <ol className="text-xs space-y-0.5" style={{ color: "var(--pxos-text-dark)" }} data-testid="recurrence-next5">
                    {result.next_5_occurrences.map((iso, i) => (
                      <li key={iso}><span className="opacity-60">{i+1}.</span> {formatItDate(iso)}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          {result && result.error && (
            <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: "#F5C24A", background: "#FFF8E1" }} data-testid="recurrence-error">
              <div className="text-sm font-medium" style={{ color: "#8A5A00" }}>Non sono riuscita a interpretare.</div>
              <div className="text-xs" style={{ color: "#8A5A00" }}>{result.error}</div>
              {result.suggestions && result.suggestions.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-70 mt-2 mb-1" style={{ color: "#8A5A00" }}>Prova con:</div>
                  <ul className="text-xs space-y-0.5">
                    {result.suggestions.map((s, i) => (
                      <li key={i}>
                        <button
                          onClick={() => setNl(s)}
                          className="underline hover:opacity-70 text-left"
                          data-testid={`recurrence-suggestion-${i}`}
                          style={{ color: "#8A5A00" }}
                        >
                          {s}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: "var(--pxos-border)" }}>
          <button onClick={onClose} className="rounded-full px-4 py-2 text-xs font-semibold border"
                  style={{ borderColor: "var(--pxos-border)", color: "var(--pxos-text-dark)" }}
                  data-testid="recurrence-cancel">
            Annulla
          </button>
          <button
            onClick={confirm}
            disabled={!result || !result.rrule}
            className="pxos-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
            data-testid="recurrence-confirm"
          >
            <Check size={14} /> Usa questa ricorrenza
          </button>
        </div>
      </div>
    </div>
  );
}
